import type { MetaFunction } from 'react-router'
import { Link } from 'react-router'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { ArticleSchema, ArticleMeta } from '~/components/seo-content'
import { FAQSchema, BreadcrumbSchema, StructuredData } from '~/components/structured-data'
import { COMPETITORS, AFFICHESERVICES } from '~/lib/competitors-data'
import { CheckCircle, ArrowRight } from 'lucide-react'

export function headers() { return SEO_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Alternative à Travaux.com, Habitatpresto, 123Devis — Comparatif 2026' },
  { name: 'description', content: 'Comparatifs honnêtes : alternative à Travaux.com, Habitatpresto, 123Devis, AlloTravaux, ManoMano Pro. AfficheServices : 110 000+ artisans, filtre RGE, médiation gratuite.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/alternatives' },
  { property: 'og:title', content: 'Alternatives aux plateformes de devis travaux 2026' },
  { property: 'og:type', content: 'website' },
]

const HUB_FAQS = [
  {
    question: "Quelle est la meilleure alternative aux plateformes de devis travaux en 2026 ?",
    answer: "AfficheServices se distingue par 3 critères clés : vérification d'identité KYC en plus du SIRET (la plupart des plateformes ne vérifient que le SIRET), filtre RGE / Qualibat / Qualifelec activable directement, et médiation gratuite incluse en cas de litige. 100 % gratuit pour les particuliers, sans inscription, avec 110 000+ artisans référencés en France.",
  },
  {
    question: "Pourquoi comparer plusieurs plateformes de devis ?",
    answer: "Chaque plateforme a un modèle économique différent qui impacte la qualité du service : volume d'artisans, niveau de vérification, granularité des filtres, support en cas de litige. Comparer permet de choisir l'outil adapté au type de projet (urgent, énergétique, lourde rénovation) et au niveau de garantie souhaité.",
  },
  {
    question: "Toutes les plateformes sont-elles gratuites pour les particuliers ?",
    answer: "Oui, les principales plateformes françaises (Travaux.com, Habitatpresto, 123Devis, AlloTravaux, ManoMano Pro, AfficheServices) facturent les artisans Pro et non les particuliers. Vérifiez tout de même que la plateforme n'impose pas d'inscription préalable ni d'engagement avant de soumettre votre demande.",
  },
  {
    question: "Comment vérifier qu'un artisan est sérieux ?",
    answer: "Trois niveaux de vérification minimum : SIRET valide (à confirmer sur societe.com via la base SIRENE de l'INSEE), assurance responsabilité civile professionnelle en cours de validité, et certification RGE pour tous les travaux énergétiques (obligatoire pour bénéficier de MaPrimeRénov', CEE, éco-PTZ). AfficheServices ajoute un quatrième niveau : la vérification d'identité KYC pour les comptes Pro et Enterprise.",
  },
  {
    question: "Que faire en cas de litige avec un artisan ?",
    answer: "Privilégiez les plateformes proposant une médiation gratuite formalisée. AfficheServices intervient comme médiateur au premier niveau. Si le litige persiste, orientez-vous vers le Médiateur de la consommation (recours gratuit obligatoire avant tout tribunal) ou la DGCCRF en cas de pratique commerciale trompeuse.",
  },
]

export default function AlternativesHub() {
  const competitors = Object.values(COMPETITORS)
  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Alternatives', url: 'https://www.afficheservices.fr/alternatives' },
      ]} />
      <ArticleSchema
        title="Alternatives aux plateformes de devis travaux 2026"
        description="Comparatifs détaillés des principales alternatives aux plateformes françaises de devis travaux."
        url="https://www.afficheservices.fr/alternatives"
      />
      <FAQSchema items={HUB_FAQS} />
      <StructuredData data={{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Alternatives aux plateformes de devis travaux',
        numberOfItems: competitors.length,
        itemListElement: competitors.map((c, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: `Alternative à ${c.name}`,
          url: `https://www.afficheservices.fr/alternatives/${c.slug}`,
        })),
      }} />

      <article className="mx-auto max-w-4xl px-4 py-10 lg:px-6">
        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-br from-primary to-blue-600 p-8 text-white">
          <p className="text-xs font-semibold uppercase tracking-wider text-blue-200">Comparatifs 2026</p>
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Alternatives aux plateformes de devis travaux</h1>
          <p className="mt-3 text-sm leading-relaxed text-blue-100 sm:text-base">
            Vous comparez les plateformes françaises de mise en relation avec des artisans ?
            Découvrez comment AfficheServices se positionne face aux acteurs historiques sur les
            critères qui comptent : vérification, filtre RGE, médiation, prix.
          </p>
        </div>

        <div className="mt-4">
          <ArticleMeta />
        </div>

        {/* Quick verdict */}
        <section className="mt-6 rounded-2xl border border-primary-200 bg-primary-50/40 p-6">
          <h2 className="text-lg font-bold text-slate-900">Pourquoi AfficheServices ?</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {AFFICHESERVICES.differentiators.map((d, i) => (
              <div key={i} className="flex gap-2">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                <div>
                  <p className="text-sm font-bold text-slate-900">{d.title}</p>
                  <p className="text-xs text-slate-600">{d.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* List of all competitors */}
        <section className="mt-8">
          <h2 className="text-xl font-bold text-slate-900">Comparatifs détaillés</h2>
          <p className="mt-2 text-sm text-slate-500">
            Chaque comparatif détaille les critères clés : vérification, RGE, médiation, volume d'artisans.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {competitors.map((c) => (
              <div key={c.slug} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs transition-colors hover:border-primary">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-base font-bold text-slate-900">{c.name}</h3>
                  <span className="text-xs text-slate-400">depuis {c.founded}</span>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-slate-600">{c.positioning}</p>
                <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                  <span className="rounded-md bg-slate-100 px-2 py-0.5 text-slate-700">{c.proCount.split(' ').slice(0, 3).join(' ')}</span>
                  {c.reviewCount && <span className="rounded-md bg-slate-100 px-2 py-0.5 text-slate-700">{c.reviewCount}</span>}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to={`/alternatives/${c.slug}`} className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-hover">
                    Alternative à {c.name} <ArrowRight className="h-3 w-3" />
                  </Link>
                  <Link to={`/vs/${c.slug}`} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-primary hover:text-primary">
                    AfficheServices vs {c.name}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison overview */}
        <section className="mt-10 overflow-hidden rounded-2xl border border-border bg-white shadow-xs">
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-lg font-bold text-slate-900">Vue d'ensemble — toutes les plateformes</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 sm:px-6">Plateforme</th>
                  <th className="px-4 py-3 text-center sm:px-6">Artisans</th>
                  <th className="px-4 py-3 text-center sm:px-6">KYC</th>
                  <th className="px-4 py-3 text-center sm:px-6">Filtre RGE</th>
                  <th className="px-4 py-3 text-center sm:px-6">Médiation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr className="bg-emerald-50/30">
                  <td className="px-4 py-3 font-bold text-emerald-900 sm:px-6">AfficheServices</td>
                  <td className="px-4 py-3 text-center text-emerald-800 sm:px-6">110 000+</td>
                  <td className="px-4 py-3 text-center text-emerald-800 sm:px-6">✓ Oui</td>
                  <td className="px-4 py-3 text-center text-emerald-800 sm:px-6">✓ Oui</td>
                  <td className="px-4 py-3 text-center text-emerald-800 sm:px-6">✓ Gratuite</td>
                </tr>
                {competitors.map((c) => (
                  <tr key={c.slug}>
                    <td className="px-4 py-3 font-medium text-slate-700 sm:px-6">{c.name}</td>
                    <td className="px-4 py-3 text-center text-slate-500 sm:px-6">{c.proCount.match(/[\d ]+/)?.[0]?.trim() || '—'}</td>
                    <td className="px-4 py-3 text-center text-slate-500 sm:px-6">Non documenté</td>
                    <td className="px-4 py-3 text-center text-slate-500 sm:px-6">{c.rgeFilter === 'native' ? '✓' : c.rgeFilter === 'limited' ? 'Limité' : 'Non doc.'}</td>
                    <td className="px-4 py-3 text-center text-slate-500 sm:px-6">{c.mediation === 'native' ? '✓' : 'Non doc.'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="border-t border-slate-100 px-6 py-3 text-xs text-slate-500">
            * "Non documenté" signifie que l'information n'est pas publiée publiquement sur la page d'accueil
            ou les pages catégorie de la plateforme. Cela ne signifie pas absence du processus.
          </p>
        </section>

        {/* CTA */}
        <section className="mt-8 rounded-2xl bg-primary-50 p-8 text-center">
          <h2 className="text-lg font-bold text-slate-900">Prêt à tester AfficheServices ?</h2>
          <p className="mt-2 text-sm text-slate-600">
            3 devis gratuits d'artisans vérifiés sous 48h. Sans inscription, sans engagement.
          </p>
          <Link
            to="/devis-gratuit"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press"
          >
            Demander 3 devis gratuits <ArrowRight className="h-4 w-4" />
          </Link>
        </section>

        {/* FAQ */}
        <section className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Questions fréquentes</h2>
          <div className="mt-4 space-y-4">
            {HUB_FAQS.map((f, i) => (
              <details key={i} className="group border-b border-slate-100 pb-4 last:border-0">
                <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900 hover:text-primary">
                  {f.question}
                  <span className="text-slate-300 group-open:rotate-45">+</span>
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </article>

      <Footer />
    </div>
  )
}
